import * as React from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import { Logo, buttonVariants, cn } from "@dosa/ui";
import { LanguageSwitch } from "../components/LanguageSwitch";

const navKeys = [
  ["home", "/"],
  ["about", "/about"],
  ["services", "/services"],
  ["howItWorks", "/how-it-works"],
  ["pricing", "/pricing"],
  ["faq", "/faq"],
  ["contact", "/contact"],
] as const;

export function PublicLayout() {
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = React.useState(false);

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-40 border-b border-charcoal-100 bg-ivory/95 backdrop-blur">
        <div className="container-app flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center" aria-label={t("brand.name")}>
            <Logo script={i18n.language === "ar" ? "ar" : "en"} />
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label={t("nav.home")}>
            {navKeys.map(([key, path]) => (
              <NavLink
                key={key}
                to={path}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive ? "text-primary-700" : "text-charcoal-700 hover:text-primary-700"
                  }`
                }
              >
                {t(`nav.${key}`)}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-2 lg:flex">
            <LanguageSwitch />
            <Link to="/login" className={cn(buttonVariants({ variant: "outline", size: "sm" }))}>
              {t("nav.login")}
            </Link>
            <Link to="/book-a-review" className={cn(buttonVariants({ size: "sm" }))}>
              {t("nav.bookReview")}
            </Link>
          </div>

          <button
            type="button"
            className="p-2 lg:hidden"
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-label="Menu"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {menuOpen && (
          <div className="border-t border-charcoal-100 bg-white lg:hidden">
            <nav className="container-app flex flex-col gap-1 py-3">
              {navKeys.map(([key, path]) => (
                <NavLink
                  key={key}
                  to={path}
                  onClick={() => setMenuOpen(false)}
                  className="rounded-md px-3 py-2.5 text-sm font-medium text-charcoal-700 hover:bg-ivory-muted"
                >
                  {t(`nav.${key}`)}
                </NavLink>
              ))}
              <div className="mt-2 flex items-center gap-2 border-t border-charcoal-100 pt-3">
                <LanguageSwitch />
              </div>
              <div className="mt-2 flex gap-2">
                <Link to="/login" className={cn(buttonVariants({ variant: "outline", size: "sm" }), "flex-1")}>
                  {t("nav.login")}
                </Link>
                <Link to="/book-a-review" className={cn(buttonVariants({ size: "sm" }), "flex-1")}>
                  {t("nav.bookReview")}
                </Link>
              </div>
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-charcoal-100 bg-charcoal-900 text-ivory">
        <div className="container-app grid gap-8 py-12 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <Logo tone="light" script={i18n.language === "ar" ? "ar" : "en"} />
            <p className="mt-3 text-sm text-charcoal-300">{t("brand.tagline")}</p>
          </div>
          <div>
            <p className="mb-3 text-sm font-semibold text-ivory">{t("nav.services")}</p>
            <ul className="space-y-2 text-sm text-charcoal-300">
              <li><Link to="/services" className="hover:text-ivory">{t("services.initialMtm.title")}</Link></li>
              <li><Link to="/services" className="hover:text-ivory">{t("services.followUp.title")}</Link></li>
              <li><Link to="/services" className="hover:text-ivory">{t("services.chronic.title")}</Link></li>
            </ul>
          </div>
          <div>
            <p className="mb-3 text-sm font-semibold text-ivory">{t("nav.about")}</p>
            <ul className="space-y-2 text-sm text-charcoal-300">
              <li><Link to="/about" className="hover:text-ivory">{t("nav.about")}</Link></li>
              <li><Link to="/faq" className="hover:text-ivory">{t("nav.faq")}</Link></li>
              <li><Link to="/contact" className="hover:text-ivory">{t("nav.contact")}</Link></li>
            </ul>
          </div>
          <div>
            <p className="mb-3 text-sm font-semibold text-ivory">{t("footer.privacy")}</p>
            <ul className="space-y-2 text-sm text-charcoal-300">
              <li><Link to="/privacy" className="hover:text-ivory">{t("footer.privacy")}</Link></li>
              <li><Link to="/terms" className="hover:text-ivory">{t("footer.terms")}</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-charcoal-700">
          <div className="container-app flex flex-col gap-2 py-5 text-xs text-charcoal-300 sm:flex-row sm:items-center sm:justify-between">
            <p>&copy; {new Date().getFullYear()} DOSA. {t("footer.rights")}</p>
            <p className="max-w-2xl sm:text-end">{t("footer.regulatoryNotice")}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
